/// <reference lib="webworker" />
// export default null;
declare let self: ServiceWorkerGlobalScope;

// Declare the injected variables from Vite
declare const __VERSION_HASH__: string;
declare const __PACKAGE_VERSION__: string;
declare const __SW_VERSION__: string;

// Use fallbacks for undefined variables
const VERSION_HASH =
	typeof __VERSION_HASH__ !== 'undefined' ? __VERSION_HASH__ : 'dev-hash';
const PACKAGE_VERSION =
	typeof __PACKAGE_VERSION__ !== 'undefined'
		? __PACKAGE_VERSION__
		: '0.0.0-dev';
const SW_VERSION =
	typeof __SW_VERSION__ !== 'undefined'
		? __SW_VERSION__
		: `${PACKAGE_VERSION}-${VERSION_HASH}`;

import { precacheAndRoute, cleanupOutdatedCaches } from 'workbox-precaching';
import { clientsClaim } from 'workbox-core';

import { registerRoute, Route, NavigationRoute } from 'workbox-routing';
import { NetworkFirst, CacheFirst, NetworkOnly } from 'workbox-strategies';
import { ExpirationPlugin } from 'workbox-expiration';
import { Queue } from 'workbox-background-sync';

// Precache the app shell (JS/CSS/HTML/icons) so the installed app opens with no network.
precacheAndRoute(self.__WB_MANIFEST);
cleanupOutdatedCaches();

// The version-file check (see App-level useBuildVersionCheck) always needs the live
// file, never a cached copy, regardless of what any other route here might match.
registerRoute(
	({ url }) => url.pathname === '/build' || url.pathname.endsWith('/build'),
	new NetworkOnly(),
);

// Artificial delay before starting *low-priority* background prefetch (data-
// driven covers/thumbnails, not necessarily on screen), so it doesn't
// contend with bandwidth the current page load still needs. High-priority
// downloads (images already rendered on screen — see DownloadPriority below)
// skip this: the content is already visible, so there's no "initial load"
// left to contend with, and the whole point of prioritizing them is defeated
// if they sit behind the same delay as everything else.
const DELAY = 5 * 1000;

console.log('Service Worker loading with version:', SW_VERSION);
swVersion();

const CACHE_PREFIX = 'cf';
const CACHE_IMAGES_NAME = `${CACHE_PREFIX}-images`;
const CACHE_AUDIOS_NAME = `${CACHE_PREFIX}-audios`;
const CACHE_VIDEOS_NAME = `${CACHE_PREFIX}-videos`;
const CACHE_FONTS_NAME = `${CACHE_PREFIX}-fonts`;
const CACHE_ASSETS_NAME = `${CACHE_PREFIX}-assets`;
const VERSION_CACHE_NAME = `${CACHE_PREFIX}-version`;

// Retries downloads interrupted by connectivity loss: replayed via the
// Background Sync API where supported, and on the SW's `online` event /
// next startup as a fallback where it isn't (Safari).
const downloadRetryQueue = new Queue('cf-download-retry-queue', {
	maxRetentionTime: 24 * 60, // minutes
	onSync: async ({ queue }) => {
		let entry = await queue.shiftRequest();
		while (entry) {
			const cacheDestination = (
				entry.metadata as { cacheName?: string } | undefined
			)?.cacheName;
			try {
				const response = await fetch(entry.request.clone());
				if (!response.ok) {
					// Permanent failure (404, 500, ...) on replay too — drop
					// it instead of requeuing forever.
					console.error(
						`Retry gave up on ${entry.request.url}: HTTP ${response.status}`,
					);
				} else if (cacheDestination) {
					const cache = await caches.open(cacheDestination);
					await cache.put(entry.request, response);
				}
			} catch (e) {
				await queue.unshiftRequest(entry);
				throw e;
			}
			entry = await queue.shiftRequest();
		}
	},
});

const imagesExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const audiosExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const videosExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const fontsExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const assetsExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const cacheName = 'canvasflow'; //cacheNames.runtime;

const registerCacheFirstRouteUsing = (
	destination: RequestDestination,
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		({ request }) => request.destination === destination,
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

const registerCacheFirstRouteUsingExtension = (
	extensions: Array<string>,
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		({ request }) => {
			extensions.join('|');
			const reg = new RegExp(`.*\\.(?:${extensions.join('|')})$`);
			return reg.test(request.url);
		},
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

const registerWebAssetsImagesCacheFirstRouteUsing = (
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		data => {
			const { request, url } = data;
			if (
				url.origin.startsWith('https://www.google') ||
				url.origin.startsWith('https://analytics.google')
			) {
				return false;
			}
			// request.destination === 'image' is the spec-correct signal (set
			// by the browser for every <img>/srcset/picture load regardless
			// of URL shape or CORS mode) and is what actually makes this
			// route fire reliably on iOS Safari — the URL-extension regex
			// alone misses any CDN that serves images via extensionless or
			// query-string-based URLs (resize/format params, no trailing
			// .jpg/.png), which is common and isn't an iOS-specific gap, just
			// one this regex-only check silently failed on everywhere.
			return (
				request.destination === 'image' ||
				/.*\.(?:png|webp|jpg|jpeg|gif)$/.test(request.url)
			);
		},
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

registerWebAssetsImagesCacheFirstRouteUsing(
	CACHE_IMAGES_NAME,
	imagesExpirationPlugin,
);
// Previously skipped on Safari entirely, leaving Safari/iOS installs with no
// offline SPA-navigation fallback at all (a request for any inner route,
// e.g. /issue/42, would just fail offline instead of serving the cached
// shell). That exclusion predates app-shell precaching; with the shell now
// precached, register it unconditionally.
//
// networkTimeoutSeconds matters here beyond hard-offline: without it,
// NetworkFirst waits for the network attempt to definitively fail before
// falling back to the precached shell -- fine in airplane mode (fails
// instantly), but on a slow/flaky connection (weak signal, subway, elevator
// -- more common in practice than literal offline) it can hang far longer
// than a user will wait before ever reaching the cache fallback that's
// sitting right there. 4s caps that wait without meaningfully hurting the
// common case (a healthy connection resolves well under that).
registerRoute(
	new NavigationRoute(
		new NetworkFirst({ cacheName, networkTimeoutSeconds: 4 }),
	),
);

registerCacheFirstRouteUsingExtension(
	['m3u8', 'ts', 'mp3'],
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);

registerCacheFirstRouteUsing(
	'audio',
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);
registerCacheFirstRouteUsing(
	'track',
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);
registerCacheFirstRouteUsing(
	'video',
	CACHE_VIDEOS_NAME,
	videosExpirationPlugin,
);

registerCacheFirstRouteUsing('font', CACHE_FONTS_NAME, fontsExpirationPlugin);

registerCacheFirstRouteUsing(
	'style',
	CACHE_ASSETS_NAME,
	assetsExpirationPlugin,
);

self.addEventListener('install', (event: ExtendableEvent) => {
	info(`SERVICE WORKER INSTALLATION: ${SW_VERSION}`);
	event.waitUntil(
		(async () => {
			// Best-effort priming of '/' — must never block skipWaiting. If this
			// rejects (transient network blip, redirect, etc.) the new worker
			// still needs to take over, otherwise it gets stuck in "waiting"
			// forever while the old worker keeps serving a previous deploy's
			// (now possibly-nonexistent) precached asset filenames.
			try {
				const cache = await caches.open(cacheName);
				await cache.addAll(['/']);
			} catch (e) {
				console.error(e);
			}
			await self.skipWaiting();
		})(),
	);
});

self.addEventListener('activate', (event: ExtendableEvent) => {
	info(`ACTIVATE SERVICE WORKER: ${SW_VERSION}`);
	event.waitUntil(Promise.all([self.clients.claim(), handleVersionUpdate()]));
});

self.addEventListener('message', (event: ExtendableMessageEvent) => {
	const { type, payload } = event.data;
	const messageType = new Set([
		'DOWNLOAD_IMAGES',
		'DOWNLOAD_BACKGROUND_IMAGES',
		'DOWNLOAD_BACKGROUND_REPLICA_PAGES',
		'DOWNLOAD_BACKGROUND_VIDEOS',
		'VERSION',
	]);

	if (!messageType.has(type)) {
		return;
	}

	info(`RECEIVE MESSAGE: "${type}"`);

	// Every handler below defers its real work behind a setTimeout (to avoid
	// contending with the initial page load's own bandwidth). Without
	// waitUntil(), the browser is free to terminate the service worker the
	// moment this synchronous handler returns — there's nothing telling it
	// work is still pending. On iOS especially, backgrounding/closing the
	// app kills the SW quickly, well inside that delay window, so the
	// download silently never happens even though the message was received.
	switch (type) {
		case 'DOWNLOAD_IMAGES':
			event.waitUntil(onDownloadImages(payload, event));
			break;
		case 'VERSION':
			swVersion();
			break;
		case 'DOWNLOAD_BACKGROUND_IMAGES':
			event.waitUntil(onDownloadBackgroundImage(payload));
			break;
		case 'DOWNLOAD_BACKGROUND_REPLICA_PAGES':
			event.waitUntil(onDownloadBackgroundPages(payload));
			break;
		case 'DOWNLOAD_BACKGROUND_VIDEOS':
			event.waitUntil(onDownloadVideos(payload));
			break;
		default:
			return;
	}
});

function delay(ms: number): Promise<void> {
	return new Promise(resolve => setTimeout(resolve, ms));
}

type DownloadPriority = 'high' | 'low';

// Shapes of the `message` event payloads this worker handles -- postMessage
// contracts with App.tsx/sw.context.tsx and the per-issue prefetch hooks
// (use-issue.ts, per-template Home.hooks.ts), not derived from a shared
// type since the message itself crosses the main-thread/worker boundary
// with no static import connecting the two sides.
interface DownloadPagesPayload {
	IssueID?: string;
	pages: Array<string>;
}

interface DownloadImagesPayload {
	images: Array<string>;
	priority?: DownloadPriority;
	IssueID?: string;
}

interface DownloadVideosPayload {
	videos: Array<string>;
	IssueID?: string;
}

const MAX_CONCURRENT_DOWNLOADS = 6;

// Every caller of downloadToCache — the DOM-visible-image scan
// (sw.context.tsx's MutationObserver) and every data-driven background
// prefetch (issue covers, article feeds, replica pages, videos) — shares
// this one scheduler instead of each call spinning up its own independent
// concurrency pool. Two problems that created otherwise:
//
// 1. Race/duplicate work: the same URL can legitimately be requested by two
//    different callers close together (most commonly: the generic DOM scan
//    sees an <img> the instant it renders, while a template's data hook
//    prefetches the same URL from the fetched issue/article payload). Two
//    independent downloadToCache() calls both running cache.match() before
//    either has cache.put() the result would both conclude "not cached yet"
//    and both fetch it. The Cache API itself is fine with that (put() on the
//    same key just overwrites — there's never more than one entry), but it's
//    a wasted duplicate network fetch, doubly wasteful on the cellular
//    connections this offline-support work exists for. inFlightDownloads
//    keyed by cache+url makes a second request for the same URL just await
//    the first one's result instead of starting a second fetch.
// 2. No prioritization: a large data-driven prefetch batch (e.g. every cover
//    in a 50-issue list) and a handful of genuinely-visible-right-now images
//    were competing for the same implicit bandwidth with no ordering. A
//    priority queue lets DOM-visible images (priority 'high') jump ahead of
//    background prefetch (priority 'low') without the prefetch batch being
//    starved outright — it still runs, just after the visible ones.
const inFlightDownloads = new Map<string, Promise<void>>();
const highPriorityQueue: Array<() => void> = [];
const lowPriorityQueue: Array<() => void> = [];
let activeDownloadWorkers = 0;

function pumpDownloadQueue(): void {
	while (activeDownloadWorkers < MAX_CONCURRENT_DOWNLOADS) {
		const next = highPriorityQueue.shift() ?? lowPriorityQueue.shift();
		if (!next) {
			return;
		}
		activeDownloadWorkers += 1;
		next();
	}
}

/**
 * Downloads a single URL into the given cache, deduplicating against any
 * identical in-flight request and respecting the shared priority queue.
 * Resolves once the URL is cached (or has permanently failed) — callers
 * don't need to know whether they triggered the fetch or piggybacked on
 * someone else's.
 */
function scheduleDownload(
	cacheDestination: string,
	url: string,
	priority: DownloadPriority,
): Promise<void> {
	const key = `${cacheDestination}::${url}`;
	const existing = inFlightDownloads.get(key);
	if (existing) {
		return existing;
	}

	const promise = new Promise<void>(resolve => {
		const run = async () => {
			try {
				const response = await fetch(url);
				if (!response.ok) {
					// Permanent failure (404, 500, ...) — retrying won't
					// help, so don't queue it, just skip it.
					console.error(
						`Failed to download ${url}: HTTP ${response.status}`,
					);
					return;
				}
				const cache = await caches.open(cacheDestination);
				await cache.put(url, response);
			} catch (e) {
				console.error(e);
				// Network-level failure (offline, connection drop): queue
				// the URL and let downloadRetryQueue replay it
				// automatically (Background Sync where supported, or on
				// the next online event otherwise).
				await downloadRetryQueue
					.pushRequest({
						request: new Request(url),
						metadata: { cacheName: cacheDestination },
					})
					.catch(console.error);
			} finally {
				inFlightDownloads.delete(key);
				activeDownloadWorkers -= 1;
				resolve();
				pumpDownloadQueue();
			}
		};
		(priority === 'high' ? highPriorityQueue : lowPriorityQueue).push(run);
	});

	inFlightDownloads.set(key, promise);
	pumpDownloadQueue();
	return promise;
}

/**
 * Single download pipeline shared by every "cache a batch of URLs" caller
 * (issue page images, background images, videos) — replaces four
 * near-identical copy-pasted functions that only differed by cache name and
 * a log message. Skips URLs already in the cache, routes the rest through
 * the shared priority queue above (so one broken URL doesn't abort the
 * whole batch, and identical URLs from concurrent callers share one fetch),
 * and optionally reports progress over the BroadcastChannel so a future
 * download-manager UI has real data to show instead of "downloading..."
 * with no percentage.
 */
async function downloadToCache(
	cacheDestination: string,
	urls: Array<string>,
	onProgress?: (completed: number, total: number, url: string) => void,
	priority: DownloadPriority = 'low',
): Promise<void> {
	const cache = await caches.open(cacheDestination);
	const urlsToDownload: Array<string> = [];
	for (const url of urls) {
		if (!(await cache.match(url))) {
			urlsToDownload.push(url);
		}
	}

	info(`Total sent to download: ${urls.length}`);
	info(`Downloading this amount: ${urlsToDownload.length}`);

	let completed = 0;
	await Promise.all(
		urlsToDownload.map(async url => {
			await scheduleDownload(cacheDestination, url, priority);
			completed += 1;
			onProgress?.(completed, urlsToDownload.length, url);
		}),
	);

	info(
		`Succeeded adding ${completed}/${urlsToDownload.length} to cache "${cacheDestination}"`,
	);
}

async function onDownloadBackgroundPages(payload: DownloadPagesPayload): Promise<void> {
	const { IssueID } = payload;
	log(`Downloading pages in service worker from the replica: ${IssueID}`);
	await delay(DELAY);
	try {
		await downloadToCache(CACHE_IMAGES_NAME, payload.pages);
		console.info(`Success downloading the pages`);
	} catch (e) {
		console.error(e);
	}
}

async function onDownloadBackgroundImage(payload: DownloadImagesPayload): Promise<void> {
	info(`Downloading images in service worker: ${payload.images.length}`);
	// Callers pass priority explicitly: the DOM-visible-image scan
	// (sw.context.tsx) sends 'high' since those images are on screen right
	// now; the data-driven prefetch hooks (use-issue.ts, per-template
	// Home.hooks.ts, etc.) don't set it and default to 'low' — they cache
	// covers/thumbnails regardless of current viewport, so they shouldn't
	// compete evenly with what's actually visible.
	const priority: DownloadPriority = payload.priority === 'high' ? 'high' : 'low';
	// High-priority requests skip the bandwidth-contention delay entirely —
	// see DELAY's definition for why. Otherwise a genuinely on-screen image
	// would sit behind the same 5s wait as an off-screen prefetch batch,
	// defeating the point of prioritizing it in the shared queue at all.
	if (priority !== 'high') {
		await delay(DELAY);
	}
	try {
		await downloadToCache(CACHE_IMAGES_NAME, payload.images, undefined, priority);
		console.info(`Success downloading the images`);
	} catch (e) {
		console.error(e);
	}
}

async function onDownloadImages(payload: DownloadImagesPayload, event: ExtendableMessageEvent): Promise<void> {
	const { images, IssueID } = payload;
	console.info(`Download images in the service worker`);
	await delay(2000);
	try {
		const clients = await self.clients.matchAll({ type: 'window' });
		console.info(`I start downloading the ${images.length} images`);
		// Explicit, port-based download request -- the caller is waiting on
		// a direct reply, so treat it as high priority.
		await downloadToCache(
			CACHE_IMAGES_NAME,
			images,
			(completed, total, image) => {
				for (const client of clients) {
					client.postMessage({
						type: 'DOWNLOAD_IMAGE',
						payload: {
							image,
							cacheName: CACHE_IMAGES_NAME,
							completed,
							total,
						},
					});
				}
			},
			'high',
		);
		console.info(
			`I finish processing all the images (${images.length}) for the issue ${IssueID}`,
		);
		event.ports[0].postMessage({
			type: 'DOWNLOAD_IMAGES',
			payload,
		});
		for (const client of clients) {
			client.postMessage({
				type: 'DOWNLOAD_IMAGES',
				payload,
			});
		}
	} catch (e) {
		event.ports[0].postMessage({
			type: 'ERROR',
			payload: {
				error: e,
			},
		});
	}
}

async function onDownloadVideos(payload: DownloadVideosPayload): Promise<void> {
	const { videos, IssueID } = payload;
	info(`Download videos in the service worker`);
	await delay(2000);
	try {
		info(`I start downloading the ${videos.length} videos`);
		await downloadToCache(CACHE_VIDEOS_NAME, videos);
		info(
			`I finish processing all the videos (${videos.length}) for the issue ${IssueID}`,
		);
	} catch (e) {
		error(e);
	}
}

// this is necessary, since the new service worker will keep on skipWaiting state
// and then, caches will not be cleared since it is not activated
if (self.skipWaiting) {
	self.skipWaiting();
}

clientsClaim();

function log(msg: unknown, ...optionalParams: unknown[]) {
	const color = '#64d1ff';
	console.log('%csw', logStyle(color), msg, ...optionalParams);
}

/*function debug(msg: unknown) {
	const color = '#0b84ff';
	console.log('%csw', logStyle(color), msg);
}*/

function info(msg: unknown) {
	const color = '#fed709';
	console.info('%csw', logStyle(color), msg);
}

function error(msg: unknown) {
	const color = '#ff375f';
	console.info('%csw', logStyle(color), msg);
}

function logStyle(color: string) {
	return `color: white; background: ${color}; border-radius: 6px; padding: 2px 12px;`;
}

function swVersion() {
	info(`SW - Version: ${SW_VERSION}`);
	info(`SW - Package Version: ${PACKAGE_VERSION}`);
	info(`SW - Version Hash: ${VERSION_HASH}`);
}


async function handleVersionUpdate() {
	try {
		// Get stored version info
		const versionCache = await caches.open(VERSION_CACHE_NAME);
		const versionResponse = await versionCache.match('version-info');

		let previousVersion = null;
		if (versionResponse) {
			const versionData = await versionResponse.json();
			previousVersion = versionData.version;
		}

		// Store current version
		await versionCache.put(
			'version-info',
			new Response(
				JSON.stringify({
					version: SW_VERSION,
					hash: VERSION_HASH,
					packageVersion: PACKAGE_VERSION,
					timestamp: Date.now(),
				}),
			),
		);

		if (previousVersion && previousVersion !== SW_VERSION) {
			info(`Version changed from ${previousVersion} to ${SW_VERSION}`);
		}
	} catch (error) {
		console.error('Error handling version update:', error);
	}
}
